import { useEffect, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Icon } from '../ui/Icon'
import { SidebarSection } from '../sidebar/SidebarSection'
import { useSections } from '../sidebar/useSections'
import { BffError, setBrowserTab, type HeldTab, type HeldTabsBrowser } from '../transport/relay'
import { PixelPet } from '../identity/PixelPet'
import { StagePage, type StageChrome } from './StagePage'
import { TabShotSheet } from './TabShotSheet'
import { byAgent, findHeld, useHeldTabs, type Held } from './heldTabs'

type Switch = { paused: boolean } | { watch: boolean }

/** What a press has asked of a row and the browser has not yet confirmed: row key → fields. */
type Pressed = Record<string, Partial<HeldTab>>

/**
 * THE CHROME TABS AGENTS HOLD — owner, 2026-10-13: 讓使用者知道他現在有哪些 Chrome Tab …
 * attach 到 Tab 上面, 上面有掛有哪些 Agent. One group per AGENT (owner, 2026-10-15: 以 Agent
 * 名稱作為樹狀圖的第一層會更好. 這樣使用者更明白該 Agent 掌控哪些分頁) — its face, its name and
 * the machine it is on — and one row per tab it holds: its title, and the browser it is in.
 *
 * And from the phone (手機端可以加: Stop/Resume, Mute/Unmute): asked what Stop means, he
 * chose PAUSE — the tab stays the agent's and every command it sends there is refused
 * until Resume. Mute is the tab's "Notify agent" switch, the popup's own.
 *
 * The list is the cloud's memory of what each browser last said (a browser that is not
 * connected has nothing listed) — heldTabs.ts, which this page shares with the corner of
 * a session whose agent holds a tab.
 *
 * A PAGE OF THE STAGE, NOT A CARD IN THE RAIL — owner, 2026-10-22: the rail holds one
 * button; the list is this page, and a tab opens in its place (TabShotSheet as a page),
 * Back at the top left coming back to the list.
 */
export function ChromeTabsPage({
  onGoToAgent,
  ...chrome
}: StageChrome & {
  /** The agent holding a tab, opened in its session (the tab page's face). */
  onGoToAgent?: (a: { session: string; hash: string; device: string }) => void
}) {
  const list = useHeldTabs()
  const { isOpen, toggle } = useSections()
  /*
   * DRAWN AT ONCE, AS PRESSED — and kept apart from the list, not written into it: the
   * list is read again whenever any browser reports (or another row's press settles), and
   * a read that lands before THIS press is confirmed would draw the row as it was.
   */
  const [pressed, setPressed] = useState<Pressed>({})
  const press = (key: string, value: Partial<HeldTab> | null) =>
    setPressed((p) => {
      const next = { ...p }
      if (value) next[key] = { ...p[key], ...value }
      else delete next[key]
      return next
    })

  const browsers = list.data?.browsers ?? []
  const count = browsers.reduce((n, b) => n + b.tabs.length, 0)

  /*
   * THE TAB, OPEN (TabShotSheet): its picture and a box to its agent. Held by name, and
   * drawn from the list as it is now, so a Pause from the browser shows on its page too;
   * a tab that left the list keeps the page on what was last known, and its picture
   * says why there is none. Its agent's other tabs are a row at its top, to move to.
   */
  const [open, setOpen] = useState<Held | null>(null)
  const openNow = open && (findHeld(browsers, open.browser.browser_id, open.told.tab) ?? open)

  if (openNow)
    return (
      <TabShotSheet
        browser={openNow.browser}
        told={openNow.told}
        onSwitch={setOpen}
        onClose={() => setOpen(null)}
        page={{ ...chrome, onBack: () => setOpen(null) }}
        {...(onGoToAgent
          ? {
              onGoToAgent: (a: { session: string; hash: string; device: string }) => {
                setOpen(null)
                onGoToAgent(a)
              },
            }
          : {})}
      />
    )

  return (
    <StagePage
      {...chrome}
      title="Chrome tabs"
      sub={count > 0 ? `${count} tab${count === 1 ? '' : 's'} given to agents` : undefined}
      backLabel="Back"
      className="more-page"
    >
      {list.isLoading && <p className="more-note">Asking your browsers…</p>}
      {list.isError && <p className="more-note more-quiet">Could not read your tabs: {String((list.error as Error)?.message ?? list.error)}</p>}
      {list.isSuccess && count === 0 && <p className="more-note">No tabs are given to agents</p>}
      {byAgent(browsers).map((g) => {
        const id = `more-tabs:agent:${g.key}`
        // An extension from before 0.2.21 lists its tabs but cannot be asked to change them: said once per such browser.
        const old = new Map(g.held.filter((h) => !h.browser.controls).map((h) => [h.browser.browser_id, h.browser.name]))
        return (
          <SidebarSection
            key={g.key}
            id={id}
            label={g.agent.session}
            sub={g.agent.machine || undefined}
            count={g.held.length}
            glyph={<PixelPet name={g.agent.session} seed={g.agent.hash} size={20} />}
            isOpen={isOpen(id)}
            onToggle={toggle}
          >
            {g.held.map(({ browser: b, told: t }) => {
              const key = `${b.browser_id}:${t.tab}`
              return (
                <TabRow key={key} browser={b} told={t} pressed={pressed[key]} onPress={(v) => press(key, v)} onOpen={() => setOpen({ browser: b, told: t })} />
              )
            })}
            {[...old].map(([browserId, name]) => (
              <p key={browserId} className="more-note more-quiet">
                Update the Avengents extension in {name} to pause or mute its tabs
              </p>
            ))}
          </SidebarSection>
        )
      })}
    </StagePage>
  )
}

function TabRow({
  browser,
  told,
  pressed,
  onPress,
  onOpen,
}: {
  browser: HeldTabsBrowser
  /** The row as the browser last said it. */
  told: HeldTab
  /** What a press here has asked and the browser has not confirmed yet. */
  pressed: Partial<HeldTab> | undefined
  onPress: (value: Partial<HeldTab> | null) => void
  /** The tab, opened: its picture and its agent (owner, 2026-10-14). */
  onOpen: () => void
}) {
  const qc = useQueryClient()
  const t = { ...told, ...pressed }
  const [failed, setFailed] = useState<string | null>(null)
  // What the browser says now has changed: a refusal shown under the row was about before.
  useEffect(() => setFailed(null), [told.paused, told.watch])

  /*
   * A refusal takes back what THIS press asked — nothing of another row's — and says why,
   * under the row. Confirmed, the press is let go once the list has been read again: the
   * browser reported before it answered, so that read already says what was asked.
   */
  const change = useMutation({
    mutationFn: (c: Switch) => setBrowserTab({ browser_id: browser.browser_id, tab: told.tab, ...c }),
    onMutate: (c) => {
      setFailed(null)
      onPress(c)
    },
    onError: (e) => {
      onPress(null)
      setFailed(e instanceof BffError && typeof e.body['message'] === 'string' ? (e.body['message'] as string) : String((e as Error)?.message ?? e))
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['browser-tabs'] })
      onPress(null)
    },
  })

  const name = t.title || `Tab ${t.tab}`
  const busy = change.isPending
  return (
    <div className={`sb-row art-row tab-row${t.paused ? ' paused' : ''}`}>
      {/* The row's words are the door to the tab; its two switches stay switches. */}
      <button type="button" className="tab-open" onClick={onOpen} title={`Open ${name}`}>
        <span className={`art-dot ${t.paused ? 'art-wait' : 'art-ok'}`} aria-hidden="true" />
        <span className="art-row-main">
          <span className="art-row-name">{name}</span>
          {/* The agent is the group's heading; the row says which browser the tab is in. */}
          <span className="art-row-sub">
            {browser.name}
            {t.paused && ' · Paused'}
          </span>
        </span>
      </button>
      {browser.controls && (
        <span className="tab-acts">
          <button
            type="button"
            className="tab-act"
            aria-label={`${t.paused ? 'Resume' : 'Pause'} ${name}`}
            title={t.paused ? 'Resume' : 'Pause'}
            disabled={busy}
            onClick={() => change.mutate({ paused: !t.paused })}
          >
            <Icon name={t.paused ? 'play_arrow' : 'pause'} size={18} />
          </button>
          <button
            type="button"
            className={`tab-act${t.watch === false ? ' off' : ''}`}
            aria-label={`${t.watch === false ? 'Unmute' : 'Mute'} ${name}`}
            title={t.watch === false ? 'Unmute' : 'Mute'}
            disabled={busy}
            onClick={() => change.mutate({ watch: t.watch === false })}
          >
            <Icon name={t.watch === false ? 'notifications_off' : 'notifications'} size={18} />
          </button>
        </span>
      )}
      {/* Outside the button: a button's contents are its name, and an alert inside one is not read out. */}
      {failed && (
        <span className="err-line tab-row-err" role="alert">
          {failed}
        </span>
      )}
    </div>
  )
}
